import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { clientService } from "@/services/api/client-service";
import { useAdminStore } from "@/stores/admin-store";
import { useQuery } from "@tanstack/react-query";
import { Building2, Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { CLIENT_ACCENT, useClientContextNavigation } from "./client-context";

interface SwitchClientDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

/**
 * Pick another client without going back to the global Clients list.
 * Opens that client's dashboard.
 */
export function SwitchClientDialog({
	open,
	onOpenChange,
}: SwitchClientDialogProps) {
	const selectedClientId = useAdminStore((s) => s.selectedClientId);
	const { enterClient } = useClientContextNavigation();
	const [search, setSearch] = useState("");
	const [debounced, setDebounced] = useState("");

	useEffect(() => {
		const handle = setTimeout(() => setDebounced(search.trim()), 250);
		return () => clearTimeout(handle);
	}, [search]);

	useEffect(() => {
		if (!open) setSearch("");
	}, [open]);

	const clients = useQuery({
		queryKey: ["clients", "switcher", debounced],
		queryFn: () => clientService.getClients(1, 25, debounced),
		enabled: open,
	});
	const items = clients.data?.items ?? [];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Switch client</DialogTitle>
					<DialogDescription>
						Opens the selected client's dashboard.
					</DialogDescription>
				</DialogHeader>
				<Input
					autoFocus
					placeholder="Search clients…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<div className="max-h-80 overflow-y-auto -mx-1">
					{clients.isLoading ? (
						<div className="flex justify-center py-6 text-slate-400">
							<Loader2 className="h-5 w-5 animate-spin" />
						</div>
					) : items.length === 0 ? (
						<div className="py-6 text-center text-sm text-slate-500">
							No clients match.
						</div>
					) : (
						<ul className="space-y-0.5">
							{items.map((client) => {
								const name = client.name || client.clientName;
								const current = client.id === selectedClientId;
								return (
									<li key={client.id}>
										<button
											type="button"
											disabled={current}
											onClick={() => {
												onOpenChange(false);
												enterClient(client.id, name);
											}}
											className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800 disabled:cursor-default disabled:hover:bg-transparent"
										>
											<span className="flex items-center gap-2 min-w-0">
												<Building2 className="h-4 w-4 text-slate-400 shrink-0" />
												<span className="truncate font-medium">
													{name}
												</span>
												{!client.isActive && (
													<span className="text-xs text-slate-400">
														(inactive)
													</span>
												)}
											</span>
											{current && (
												<span
													className={`flex items-center gap-1 text-xs ${CLIENT_ACCENT.currentMark}`}
												>
													<Check className="h-3.5 w-3.5" />
													Current
												</span>
											)}
										</button>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
